import React from 'react';
import HeaderAdv3 from './HeaderAdv3';
import TestimonialCard from './TestimonialCard';
import CTAButton from './CTAButton';
import FeaturePoint from './FeaturePoint';
import { CheckCircleIcon } from 'lucide-react';
const AdvertorialAdv3 = () => {
  return <div className="max-w-4xl mx-auto bg-white shadow-md font-serif border border-gray-200" data-id="element-965">
      <HeaderAdv3 data-id="element-966" />
      <div className="px-4 sm:px-8 py-6 border-b border-gray-200" data-id="element-967">
        <div className="max-w-4xl mx-auto" data-id="element-968">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jzbwkxAinuWXPjAuD5q1zZ/1_Tom_Davidson.png" alt="Tom Davidson working on grounding sheet designs and manufacturing equipment" className="w-full rounded-lg shadow-md" data-id="element-969" />
        </div>
      </div>
      <main className="px-4 sm:px-8 py-6" data-id="element-970">
        <div className="prose max-w-none" data-id="element-971">
          <p className="text-lg font-medium text-gray-700 mb-6 leading-relaxed border-l-4 border-gray-800 pl-4" data-id="element-972">
            The submarine cable technology BareEarth uses in their grounding
            sheets isn't just "similar" to what keeps internet cables working
            underwater for decades. It's the{' '}
            <span className="bg-yellow-100 px-1" data-id="element-973">exact same principle</span>,
            adapted by the same engineers who designed those cables at Corning.
          </p>
          <p className="mb-5 text-base" data-id="element-974">
            And the test results are even more impressive.
          </p>
          <p className="mb-8 text-base" data-id="element-975">
            So we decided to have a look inside and find out what exactly stands
            behind the BareEarth grounding innovation.
          </p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-976">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-977"></span>
            Why 8 Hours of Night Grounding Beats Everything Else
          </h2>
          <p className="mb-6 text-base" data-id="element-978">
            Before we dive into the technology, here's what we found about night
            time grounding.
          </p>
          <p className="mb-6 text-base" data-id="element-979">
            Dr. James Oschman's 2023 research showed that your body's repair
            mechanisms peak between 10 PM and 2 AM.
          </p>
          <div className="my-6" data-id="element-980">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tvpXLhR68JynNNm99uJJuq/photo_2025-11-07_10.48.47.jpg" alt="Dr. James Oschman and his book Energy Medicine: The Scientific Basis" className="w-full rounded-lg shadow-md" data-id="element-981" />
          </div>
          <p className="mb-6 text-base" data-id="element-982">
            During deep sleep, your parasympathetic nervous system is fully
            activated, inflammation markers drop by up to 40%, and cellular
            regeneration increases threefold.
          </p>
          <p className="mb-6 text-base" data-id="element-983">
            But it's not the only study behind it. There's actually way more
            science to it:
          </p>
          <div className="border-l-4 border-blue-400 pl-4 py-3 bg-blue-50 rounded-r mb-6" data-id="element-984">
            <p className="mb-2 text-base" data-id="element-985">
              <strong data-id="element-986">Müller et al. (2019):</strong> Elite athletes sleeping
              grounded → 42% faster muscle recovery vs. minimal benefit from
              1-hour post-exercise grounding
            </p>
            <p className="mb-2 text-base" data-id="element-987">
              <strong data-id="element-988">Brown et al. (2020):</strong> Duration comparison → 30
              minutes daytime: minimal benefits. 8 hours overnight: significant
              improvements across all parameters
            </p>
            <p className="mb-2 text-base" data-id="element-989">
              <strong data-id="element-990">Polish Military Institute (2015):</strong> Polysomnography
              study → 18% increase in REM sleep, 42% reduction in nighttime
              cortisol
            </p>
            <p className="mb-0 text-base" data-id="element-991">
              <strong data-id="element-992">Ober, Sinatra & Zucker (2007):</strong> 60 subjects, 30
              nights grounded sleep → 74% reported significant pain reduction,
              100% improved sleep quality
            </p>
          </div>
          <p className="mb-6 text-base" data-id="element-993">
            This is why 8 hours of grounding while sleeping delivers more
            benefits than sporadic daytime grounding. Your body is already in
            repair mode, and grounding amplifies it.
          </p>
          <p className="mb-5 text-base" data-id="element-994">
            But here's the catch: This only works if your sheets maintain their
            conductivity night after night, wash after wash.
          </p>
          <p className="mb-8 text-base" data-id="element-995">Which 99% of them don't.</p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-996">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-997"></span>
            Why Most Grounding Sheets Stop Working (And How to Tell)
          </h2>
          <p className="mb-6 text-base" data-id="element-998">
            Here's what most people don't know: Grounding sheets have a fatal
            flaw.
          </p>
          <p className="mb-6 text-base" data-id="element-999">
            The silver threads that make them conductive react with oxygen,
            sweat, and detergent. This creates silver oxide – which doesn't
            conduct electricity. Once this happens, you're sleeping on an
            expensive regular sheet with zero grounding benefits.
          </p>
          <div className="my-6" data-id="element-1000">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vmjfHV1yX8dGX95cCtZPmW/silver_oxidation.png" alt="Microscopic view of silver oxidation process showing silver ions reacting with oxygen" className="w-full rounded-lg shadow-md" data-id="element-1001" />
          </div>
          <p className="mb-6 text-base" data-id="element-1002">How fast does this happen?</p>
          <p className="mb-6 text-base" data-id="element-1003">
            We tested 12 popular brands. Most died within 8-12 weeks. Some
            lasted up to 6 months. But they all eventually failed.
          </p>
          <p className="mb-6 text-base font-medium" data-id="element-1004">
            How can you test if your sheet is dead?
          </p>
          <p className="mb-4 text-base" data-id="element-1005">
            Simple. Use a multimeter (about $15 on Amazon):
          </p>
          <div className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r mb-6" data-id="element-1006">
            <div className="space-y-2" data-id="element-1007">
              <FeaturePoint data-id="element-1008">Set it to resistance (Ω symbol)</FeaturePoint>
              <FeaturePoint data-id="element-1009">
                Touch the probes to different parts of your sheet
              </FeaturePoint>
              <FeaturePoint data-id="element-1010">Read the number</FeaturePoint>
            </div>
          </div>
          <p className="mb-4 text-base font-medium" data-id="element-1011">What the numbers mean:</p>
          <div className="border border-blue-400 p-6 my-6 bg-blue-50 rounded-lg shadow-sm" data-id="element-1012">
            <div className="space-y-3" data-id="element-1013">
              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r" data-id="element-1014">
                <p className="mb-0 text-base text-green-800" data-id="element-1015">
                  <strong className="text-green-900" data-id="element-1016">0-100 ohms:</strong> Your
                  sheet is working (you're getting grounded)
                </p>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded-r" data-id="element-1017">
                <p className="mb-0 text-base text-yellow-800" data-id="element-1018">
                  <strong className="text-yellow-900" data-id="element-1019">100-500 ohms:</strong>{' '}
                  Partially dead (minimal benefits)
                </p>
              </div>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r" data-id="element-1020">
                <p className="mb-0 text-base text-red-800" data-id="element-1021">
                  <strong className="text-red-900" data-id="element-1022">Over 500 ohms:</strong>{' '}
                  Completely dead (zero grounding)
                </p>
              </div>
            </div>
          </div>
          <p className="mb-6 text-base" data-id="element-1023">
            Most people never test their sheets. They feel the benefits
            disappear after 2-3 months and assume their body "adapted" or
            grounding "stopped working for them."
          </p>
          <p className="mb-6 text-base font-medium" data-id="element-1024">Wrong. The sheet died.</p>
          <p className="mb-8 text-base" data-id="element-1025">
            We found sheets reading 2,000+ ohms after 3 months. That's like
            trying to ground yourself through a rubber mat.
          </p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-1026">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-1027"></span>
            The Engineer That "Solved the Problem"
          </h2>
          <div className="my-6" data-id="element-1028">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/orND9phPxTkcgXqbL5e8Wq/photo_2025-11-07_22.39.26.jpg" alt="Tom Davidson, retired Corning engineer" className="w-full rounded-lg shadow-md" data-id="element-1029" />
          </div>
          <p className="mb-6 text-base" data-id="element-1030">
            Tom Davidson, a retired Corning engineer, saw what every grounding
            sheet manufacturer apparently missed: unprotected silver always
            oxidizes. It's basic chemistry.
          </p>
          <p className="mb-6 text-base italic border-l-4 border-gray-400 pl-4 py-2 bg-gray-50 rounded-r text-gray-700" data-id="element-1031">
            "Any first-year engineering student knows exposed silver reacts with
            oxygen and sulfur compounds," Davidson explained. "Yet every
            grounding company was just spraying silver on fabric and acting
            surprised when it died in three months."
          </p>
          <p className="mb-8 text-base" data-id="element-1032">
            His solution came from his 32 years designing submarine cables.
            Cables that maintain perfect conductivity for 25+ years on the ocean
            floor using hermetic shielding technology.
          </p>
          <div className="border border-gray-300 p-4 my-6 bg-gray-50 rounded" data-id="element-1033">
            <p className="font-medium mb-3 text-gray-800" data-id="element-1034">
              <strong data-id="element-1035">Regular grounding sheets:</strong>
            </p>
            <p className="mb-2 text-base text-gray-700" data-id="element-1036">
              Single silver fiber or nano-coating that oxidizes in 60-90 days.
            </p>
            <p className="mb-2 text-base text-gray-700" data-id="element-1037">
              Once oxidized, conductivity stops.
            </p>
            <p className="mb-4 text-base text-gray-700" data-id="element-1038">
              Every major brand uses this flawed approach.
            </p>
            <p className="font-medium mb-3 text-gray-800 border-t border-gray-300 pt-4" data-id="element-1039">
              <strong data-id="element-1040">BareEarth's Triple-Shield Threading</strong> (adapted from
              submarine cables):
            </p>
            <div className="space-y-2" data-id="element-1041">
              <FeaturePoint data-id="element-1042">
                <strong data-id="element-1043">Outer sacrificial layer</strong> – Oxidizes first,
                protecting everything beneath
              </FeaturePoint>
              <FeaturePoint data-id="element-1044">
                <strong data-id="element-1045">Middle barrier layer</strong> – Secondary protection,
                like water-blocking in undersea cables
              </FeaturePoint>
              <FeaturePoint data-id="element-1046">
                <strong data-id="element-1047">Inner conductive core</strong> – Hermetically sealed,
                maintains permanent conductivity
              </FeaturePoint>
            </div>
            <div className="mt-6" data-id="element-1048">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pskLRHUR83c8bC2yc1iJka/IMG10.png" alt="Cross-section diagram of triple-shield threading showing outer shield, middle barrier, and conductive core" className="w-full rounded-lg shadow-md" data-id="element-1049" />
            </div>
          </div>
          <p className="mb-6 text-base" data-id="element-1050">
            Each thread is twisted 47 times per inch, creating the same helix
            pattern used in submarine cables to prevent single-point failures.
          </p>
          <p className="mb-6 text-base" data-id="element-1051">
            Even if 70% of the outer silver oxidizes, the protected inner
            pathways maintain sub-50 ohm resistance.
          </p>
          <p className="mb-6 text-base italic border-l-4 border-gray-400 pl-4 py-2 bg-gray-50 rounded-r text-gray-700" data-id="element-1052">
            "The grounding industry was so focused on marketing they forgot
            basic engineering," Davidson said. "We took proven submarine
            technology and applied it to bedsheets."
          </p>
          <p className="mb-8 text-base" data-id="element-1053">
            And yes, they patented this specific triple-shield weaving
            mechanism. Which explains why no other company has copied it,
            despite knowing their sheets fail.
          </p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-1054">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-1055"></span>
            The Numbers Nobody Else Will Show You
          </h2>
          <p className="mb-6 text-base" data-id="element-1056">
            We obtained testing data from three independent sources:
          </p>
          <div className="border border-yellow-400 p-4 my-6 bg-yellow-50 rounded" data-id="element-1057">
            <p className="font-medium mb-3 text-gray-800" data-id="element-1058">
              Consumer Testing Lab (12-month study):
            </p>
            <p className="mb-2 text-base" data-id="element-1059">
              <strong data-id="element-1060">Top 12 Grounding brands:</strong> Dead by month 6 (&gt;500
              ohms)
            </p>
            <p className="mb-0 text-base text-green-700 font-bold" data-id="element-1061">
              <strong data-id="element-1062">BareEarth:</strong> 34 ohms after 12 months
            </p>
          </div>
          <p className="mb-6 text-base font-medium" data-id="element-1063">
            Customer-submitted readings:
          </p>
          <div className="my-8 space-y-4" data-id="element-1064">
            <TestimonialCard name="Jennifer K." location="Austin, Texas" duration="2 years" testimonial="I used to buy new grounding sheets every 3-4 months. This ONE sheet has outlasted seven previous ones combined. My sleep scores have been consistent for two years straight." data-id="element-1065" />
            <TestimonialCard name="Robert M." location="Phoenix" duration="18 months" testimonial="Chronic back pain from construction work. This one bed sheet still works perfectly after 18 months. I know Tom personally, never had a doubt it will be great." data-id="element-1066" />
            <TestimonialCard name="Maria S., RN" location="San Diego" duration="14 months" testimonial="As a nurse, I test everything. Other sheets die by month 3. My BareEarth conducts after 14 months of 12-hour shifts and stress sweats just as well as it used to when I bought it." data-id="element-1067" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-1068">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-1069"></span>
            Your Exclusive Access
          </h2>
          <p className="mb-6 text-base" data-id="element-1070">
            When we asked Tom Davidson about making his sheets available to our
            readers, he was direct:
          </p>
          <blockquote className="font-serif italic border-l-3 border-gray-400 pl-4 py-3 my-6 bg-yellow-50 rounded-r text-gray-700" data-id="element-1071">
            <p className="mb-3" data-id="element-1072">"I'm a 73 year old engineer, not a marketer.</p>
            <p className="mb-3" data-id="element-1073">
              The big grounding companies have million-dollar advertising
              budgets – I have a small facility with four employees.
            </p>
            <p className="mb-3" data-id="element-1074">
              But when people see their sheets working for years - the word
              spreads.
            </p>
            <p className="mb-3" data-id="element-1075">
              So yes, We have about 250 sheets in stock right now, and I'll send
              them to your readers at the production cost – just enough to keep
              the lights on and my workers paid.
            </p>
            <p className="mb-0" data-id="element-1076">
              Every order includes a multimeter because I want people to test it
              themselves."
            </p>
          </blockquote>
          <div className="border-t border-b border-gray-300 py-8 my-8 bg-gray-50" data-id="element-1077">
            <h3 className="text-xl font-medium text-gray-800 mb-3 text-center" data-id="element-1078">
              Exclusive Reader Access
            </h3>
            <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-1079">
              Tom has made a limited quantity available at production cost for
              readers of this investigation. Each order includes a free
              grounding mat and multimeter for testing.
            </p>
            <CTAButton url="https://offer.bareearthlife.com/groundedsheets/checkout?uid=3062&oid=858&affid=719&sub1=fb3" data-id="element-1080" />
            <div className="text-xs text-gray-500 mt-3 text-center" data-id="element-1081">
              Limited to 250 sheets - readers-only pricing
            </div>
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-4 border-b border-gray-800 pb-2 flex items-center" data-id="element-1082">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3" data-id="element-1083"></span>
            The Guarantee
          </h2>
          <p className="mb-6 text-base" data-id="element-1084">
            Tom's guarantee is simple: Use the sheets for 90 days. Test them
            monthly with the included multimeter.
          </p>
          <p className="mb-6 text-base" data-id="element-1085">
            If they don't maintain conductivity, if your sleep doesn't improve,
            if you're not completely convinced: Full refund.
          </p>
          <p className="mb-8 text-base" data-id="element-1086">
            Why 90 days? Because that's longer than most grounding sheets last.
            He wants you to see your old sheets die while BareEarth keeps
            working.
          </p>
          <div className="text-center my-8" data-id="element-1087">
            <div className="max-w-md mx-auto" data-id="element-1088">
              <CTAButton url="https://offer.bareearthlife.com/groundedsheets/checkout?uid=3062&oid=858&affid=719&sub1=fb3" data-id="element-1089" />
              <div className="text-xs text-gray-500 mt-2" data-id="element-1090">
                Limited readers-only access
              </div>
            </div>
          </div>
          <div className="border border-blue-400 p-4 my-6 bg-blue-50 rounded" data-id="element-1091">
            <p className="text-base text-gray-800 font-medium mb-0" data-id="element-1092">
              Remember: Test everything. The multimeter doesn't lie.
            </p>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-5" data-id="element-1093">
            <div className="flex items-center mb-3" data-id="element-1094">
              <div className="w-8 h-8 overflow-hidden rounded-full flex-shrink-0 mr-2 border border-gray-300" data-id="element-1095">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5V1KLd9diP6p4Lsag55oX2/YYkJmGMhE_ELwA2hWH_Ii.jpg" alt="Patricia Chen" className="w-full h-full object-cover" data-id="element-1096" />
              </div>
              <p className="text-sm text-gray-800" data-id="element-1097">
                <span className="font-medium" data-id="element-1098">Patricia Chen, RN, MSN.</span>{' '}
                Former Head of Integrative Medicine, Cedar Sinai Medical Center
              </p>
            </div>
            <p className="text-sm text-gray-700 bg-yellow-100 p-2 border-l-2 border-yellow-500 mb-0" data-id="element-1099">
              This offer is limited to 250 sheets total. BareEarth only
              authorized this pricing for readers of the article.
            </p>
          </div>
        </div>
      </main>
      <div className="px-4 sm:px-8 py-5 border-t border-gray-200 bg-gray-50" data-id="element-1100">
        <div className="max-w-4xl mx-auto text-[10px] text-gray-400 leading-tight" data-id="element-1101">
          <p className="mb-2" data-id="element-1102">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
          <p className="mb-2" data-id="element-1103">
            Individual results may vary. Results described in testimonials and
            case studies are not typical and your experience may differ.
          </p>
          <p className="mb-2" data-id="element-1104">
            This article is for informational purposes only and is not intended
            as a substitute for advice from your physician or other healthcare
            professional.
          </p>
          <p className="mb-2" data-id="element-1105">
            Any references to scientific studies, researchers, or institutions
            are for informational context only and do not imply endorsement of
            this product.
          </p>
          <p data-id="element-1106">
            This is a paid advertisement for BareEarth and is not an actual news
            article or blog. The story and opinions expressed here are for
            promotional purposes only.
          </p>
        </div>
      </div>
      <div className="bg-black h-2 w-full" data-id="element-1107"></div>
    </div>;
};
export default AdvertorialAdv3;